import { useId } from 'react';

// The ValueGraph brand mark — "the Sweep": three nested arcs building
// outward, conviction ramping by saturation (lightness constant) via the
// pine→teal tide gradients. Geometry and colours are fixed brand assets
// (docs/brand.md) — never recolor, re-stroke, box, or shadow the mark.
// sm → header lockup, lg → login/stacked lockup.
//
// The viewBox is cropped to the arcs' ink (round stroke caps included) rather
// than the nominal 64×64 grid the paths are drawn on, so the mark ships no
// built-in padding and lockups own their spacing. SIZES are rendered widths;
// the height follows the crop's aspect.
const VIEW = { x: 7, y: 18, w: 50, h: 28 } as const;
const SIZES = { sm: 26, md: 37, lg: 55 } as const;

// Per-arc gradient stops from the guidelines: saturation ramps inner→outer.
const STOPS = {
  outer: ['oklch(0.34 0.065 175)', 'oklch(0.70 0.125 168)'],
  mid: ['oklch(0.34 0.030 175)', 'oklch(0.70 0.062 168)'],
  inner: ['oklch(0.34 0.005 175)', 'oklch(0.68 0.009 168)'],
} as const;

export function BrandMark({ size = 'sm', className }: { size?: keyof typeof SIZES; className?: string }) {
  // Gradient defs are per-instance (useId) so two marks on one page can't
  // collide on SVG ids.
  const id = useId();
  const width = SIZES[size];
  return (
    <svg
      width={width}
      height={(width * VIEW.h) / VIEW.w}
      viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id={`${id}-out`} gradientUnits="userSpaceOnUse" x1="10" y1="46" x2="54" y2="20">
          <stop offset="0" stopColor={STOPS.outer[0]} />
          <stop offset="1" stopColor={STOPS.outer[1]} />
        </linearGradient>
        <linearGradient id={`${id}-mid`} gradientUnits="userSpaceOnUse" x1="14" y1="44" x2="50" y2="26">
          <stop offset="0" stopColor={STOPS.mid[0]} />
          <stop offset="1" stopColor={STOPS.mid[1]} />
        </linearGradient>
        <linearGradient id={`${id}-in`} gradientUnits="userSpaceOnUse" x1="22" y1="43" x2="42" y2="34">
          <stop offset="0" stopColor={STOPS.inner[0]} />
          <stop offset="1" stopColor={STOPS.inner[1]} />
        </linearGradient>
      </defs>
      <path d="M24.75 43 A7.25 7.25 0 0 1 39.25 43" stroke={`url(#${id}-in)`} strokeWidth="2.6" strokeLinecap="round" fill="none" />
      <path d="M17.8 43 A14.2 14.2 0 0 1 46.2 43" stroke={`url(#${id}-mid)`} strokeWidth="3.8" strokeLinecap="round" fill="none" />
      <path d="M10.3 43 A21.7 21.7 0 0 1 53.7 43" stroke={`url(#${id}-out)`} strokeWidth="5.2" strokeLinecap="round" fill="none" />
    </svg>
  );
}
